import React, { useEffect, useState } from 'react';
import { api, apiErr } from '@/lib/api';
import { usePeriod } from '@/context/PeriodContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, Paperclip } from 'lucide-react';
import PeriodPicker from '@/components/PeriodPicker';
import { fmtMXN, fmtDateMX } from '@/lib/format';

export default function Expenses() {
  const { year, month } = usePeriod();
  const [period, setP] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', concept: '', amount: '', iva_acreditable: '', has_invoice: false, uuid: '', is_currency_commission: false, notes: '' });
  const [attachFile, setAttachFile] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const p = await api.get(`/periods/lookup/${year}/${month}`);
    setP(p.data);
    const r = await api.get(`/expenses?period_id=${p.data.id}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [year, month]);

  const startNew = () => { setEditing(null); setAttachFile(null); setForm({ date: today, concept: '', amount: '', iva_acreditable: '', has_invoice: false, uuid: '', is_currency_commission: false, notes: '' }); setOpen(true); };
  const startEdit = (e) => { setEditing(e); setAttachFile(null); setForm({ ...e }); setOpen(true); };

  const save = async () => {
    if (!form.date || !form.concept || !form.amount) { toast.error('Completa fecha, concepto y monto'); return; }
    const payload = {
      period_id: period.id, date: form.date, concept: form.concept,
      amount: parseFloat(form.amount || 0), iva_acreditable: parseFloat(form.iva_acreditable || 0),
      has_invoice: !!form.has_invoice, uuid: form.uuid || '',
      is_currency_commission: !!form.is_currency_commission, notes: form.notes || '',
    };
    try {
      let id;
      if (editing) { await api.patch(`/expenses/${editing.id}`, payload); id = editing.id; }
      else { const r = await api.post('/expenses', payload); id = r.data.id; }
      if (attachFile) {
        const fd = new FormData();
        fd.append('entity_type', 'expense'); fd.append('entity_id', id); fd.append('period_id', period.id); fd.append('file', attachFile);
        await api.post('/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Gasto guardado');
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar gasto?')) return;
    try { await api.delete(`/expenses/${id}`); toast.success('Eliminado'); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PeriodPicker/>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Gastos</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Facturas recibidas y comisiones</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button data-testid="btn-add-expense" onClick={startNew} className="h-11 rounded-full px-5"><Plus className="h-4 w-4 mr-1"/> Nuevo</Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto">
            <SheetHeader><SheetTitle className="font-display text-left">{editing ? 'Editar gasto' : 'Nuevo gasto'}</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-4 pb-8">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Fecha</Label>
                <Input type="date" data-testid="exp-date" className="h-12 mt-1" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Concepto</Label>
                <Input data-testid="exp-concept" className="h-12 mt-1" placeholder="Descripción" value={form.concept} onChange={(e) => setForm(f => ({ ...f, concept: e.target.value }))}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Monto</Label>
                  <Input type="number" inputMode="decimal" data-testid="exp-amount" className="h-12 mt-1 num" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}/>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">IVA acreditable</Label>
                  <Input type="number" inputMode="decimal" data-testid="exp-iva" className="h-12 mt-1 num" value={form.iva_acreditable} onChange={(e) => setForm(f => ({ ...f, iva_acreditable: e.target.value }))}/>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Tiene factura</p>
                  <p className="text-xs text-muted-foreground">CFDI recibido</p>
                </div>
                <Switch data-testid="exp-has-invoice" checked={form.has_invoice} onCheckedChange={(v) => setForm(f => ({ ...f, has_invoice: v }))}/>
              </div>
              {form.has_invoice && (
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">UUID</Label>
                  <Input data-testid="exp-uuid" className="h-12 mt-1" placeholder="Folio fiscal" value={form.uuid} onChange={(e) => setForm(f => ({ ...f, uuid: e.target.value }))}/>
                </div>
              )}
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Comisión por cambio de monedas</p>
                  <p className="text-xs text-muted-foreground">Marca si aplica</p>
                </div>
                <Switch data-testid="exp-commission" checked={form.is_currency_commission} onCheckedChange={(v) => setForm(f => ({ ...f, is_currency_commission: v }))}/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Adjuntar (ticket / factura)</Label>
                <label className="mt-1 h-12 rounded-lg border border-dashed border-border flex items-center justify-center gap-2 cursor-pointer text-sm text-muted-foreground">
                  <Paperclip className="h-4 w-4"/>
                  <span data-testid="exp-file-label">{attachFile ? attachFile.name : 'Elegir archivo (opcional)'}</span>
                  <input data-testid="exp-file" type="file" className="hidden" accept="image/*,application/pdf,application/xml,text/csv" onChange={(e) => setAttachFile(e.target.files?.[0] || null)}/>
                </label>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Notas</Label>
                <Textarea data-testid="exp-notes" className="mt-1" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}/>
              </div>
              <Button data-testid="exp-save" onClick={save} className="w-full h-12 rounded-full">Guardar</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center border-dashed"><p className="text-sm text-muted-foreground">Sin gastos este mes.</p></Card>
      ) : (
        <div className="space-y-2" data-testid="expenses-list">
          {[...items]
          .sort((a, b) => String(b.date).localeCompare(String(a.date)))
          .map((e) => (
            <Card key={e.id} className="p-4 border-border rounded-lg" data-testid={`exp-item-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{e.concept}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateMX(e.date)} · IVA acr. <span className="num">{fmtMXN(e.iva_acreditable)}</span></p>
                  <div className="flex gap-2 mt-1">
                    {e.has_invoice ? <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary rounded px-1.5 py-0.5">Con factura</span> : <span className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-700 dark:text-amber-500 rounded px-1.5 py-0.5">Sin factura</span>}
                    {e.is_currency_commission && <span className="text-[10px] uppercase tracking-wider bg-secondary rounded px-1.5 py-0.5">Comisión cambio</span>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-lg font-bold num">{fmtMXN(e.amount)}</p>
                  <div className="flex gap-1 justify-end mt-1">
                    <button data-testid={`exp-edit-${e.id}`} onClick={() => startEdit(e)} className="p-1.5 rounded hover:bg-secondary"><Edit3 className="h-3.5 w-3.5"/></button>
                    <button data-testid={`exp-delete-${e.id}`} onClick={() => remove(e.id)} className="p-1.5 rounded hover:bg-secondary text-destructive"><Trash2 className="h-3.5 w-3.5"/></button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
